<template>
  <div>
    <div class="mfg-header">
      <h4 class="mfg-title text-primary">{{ standalone ? 'Deliveries' : 'Delivery Notes' }}</h4>
      <button class="btn btn-primary" @click="openDispatch()"><i class="bi bi-truck mr-1"></i>Dispatch an invoice</button>
    </div>
    <p class="text-muted small">
      Invoiced goods leave stock when you dispatch them here (shown in Inventory Activity). The client and the driver can get an SMS link to view, sign or confirm the delivery with a code — no Dukakit account needed. The invoice itself is not changed.
    </p>

    <div class="filter-bar">
      <input class="form-control" v-model="search" @input="debouncedLoad" placeholder="Delivery note number" style="flex:1" />
    </div>

    <div v-if="loading" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></div>
    <div v-else class="table-responsive mfg-box">
      <table class="table table-hover mb-0">
        <thead><tr><th>Number</th><th>Invoice</th><th>Customer</th><th>Destination</th><th>Dispatched</th><th>Status</th><th></th></tr></thead>
        <tbody>
          <tr v-for="n in notes" :key="n.id" class="dn-row" @click="openDetails(n)">
            <td class="font-weight-bold text-primary">{{ n.number }}</td>
            <td>{{ n.invoice && n.invoice.number }}</td>
            <td>{{ n.customer && n.customer.full_name }}</td>
            <td class="small">{{ n.destination || '—' }}
              <div v-if="n.delivered_at" class="text-secondary">Received by {{ n.received_by_name }}<span v-if="n.confirmation_method"> · {{ methodLabel(n.confirmation_method) }}</span></div>
            </td>
            <td class="small">{{ (n.dispatched_at || '').substring(0, 16).replace('T', ' ') }}</td>
            <td><span class="badge" :class="n.status === 'delivered' ? 'badge-primary' : n.status === 'dispatched' ? 'badge-dark' : 'badge-secondary'">{{ n.status === 'dispatched' ? 'in transit' : n.status.replace('_', ' ') }}</span></td>
            <td class="text-right text-nowrap" @click.stop>
              <button class="btn btn-sm btn-outline-dark" title="View details" @click="openDetails(n)"><i class="bi bi-eye"></i></button>
              <a class="btn btn-sm btn-outline-dark" :href="printUrl(n.id)" target="_blank" title="Print"><i class="bi bi-printer"></i></a>
              <button v-if="n.status === 'dispatched'" class="btn btn-sm btn-outline-dark" title="Links & SMS" @click="openShare(n)"><i class="bi bi-chat-dots"></i></button>
              <button v-if="n.status === 'dispatched'" class="btn btn-sm btn-primary" @click="openDelivered(n)">Sign / mark delivered</button>
              <button class="btn btn-sm btn-outline-secondary" @click="openReturn(n)">Return</button>
            </td>
          </tr>
          <tr v-if="!notes.length"><td colspan="7" class="text-center text-muted py-4">No delivery notes yet.</td></tr>
        </tbody>
      </table>
    </div>

    <!-- Details -->
    <b-modal ref="detailsModal" size="xl" hide-footer :title="detail ? 'Delivery note ' + detail.number : 'Delivery note'">
      <div v-if="detailLoading" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></div>
      <div v-else-if="detail">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-start flex-wrap mb-3">
          <div>
            <div class="h5 mb-0">{{ detail.customer ? detail.customer.full_name : 'No customer' }}</div>
            <div class="small text-secondary">{{ detail.destination || 'No destination recorded' }}<span v-if="detail.destination_contact"> · Contact: {{ detail.destination_contact }}</span></div>
          </div>
          <span class="badge p-2" :class="statusBadge(detail.status)">{{ statusText(detail.status) }}</span>
        </div>

        <!-- Key figures -->
        <div class="dn-stats mb-3">
          <div><div class="dn-stat-label">Value delivered</div><div class="dn-stat-value">{{ money(sum.value_delivered) }}</div></div>
          <div v-if="sum.cost_of_goods > 0"><div class="dn-stat-label">Cost of goods</div><div class="dn-stat-value">{{ money(sum.cost_of_goods) }}</div></div>
          <div v-if="sum.invoice"><div class="dn-stat-label">Invoice total</div><div class="dn-stat-value">{{ money(sum.invoice.total) }}</div></div>
          <div v-if="sum.invoice && sum.invoice.balance !== null"><div class="dn-stat-label">Invoice balance</div>
            <div class="dn-stat-value" :class="sum.invoice.balance > 0 ? 'text-danger' : 'text-primary'">{{ money(sum.invoice.balance) }}</div></div>
          <div><div class="dn-stat-label">Still to deliver</div>
            <div class="dn-stat-value" :class="{ 'text-danger': sum.outstanding_lines > 0 }">{{ sum.outstanding_lines ? sum.outstanding_lines + ' line(s)' : 'Nothing' }}</div></div>
        </div>

        <div class="row">
          <div class="col-lg-8">
            <!-- Items -->
            <table class="table table-sm">
              <thead><tr><th>Item</th><th class="text-right">This note</th><th class="text-right">Returned</th><th class="text-right">Invoiced</th><th class="text-right">Delivered so far</th><th class="text-right">Still to deliver</th></tr></thead>
              <tbody>
                <tr v-for="i in detail.items" :key="i.id">
                  <td>{{ i.product && i.product.product }}
                    <div v-if="lotsOf(i)" class="small text-secondary">Batch {{ lotsOf(i) }}</div></td>
                  <td class="text-right text-nowrap">{{ qty(i.quantity) }} {{ i.product && i.product.measuring_unit }}
                    <div v-if="i.entered_unit && i.product && i.entered_unit !== i.product.measuring_unit" class="small text-secondary">({{ qty(i.entered_quantity) }} {{ i.entered_unit }})</div></td>
                  <td class="text-right" :class="{ 'text-danger': i.returned_quantity > 0 }">{{ i.returned_quantity > 0 ? qty(i.returned_quantity) : '—' }}</td>
                  <td class="text-right">{{ i.invoiced_quantity !== null ? qty(i.invoiced_quantity) : '—' }}</td>
                  <td class="text-right">{{ i.delivered_to_date !== null ? qty(i.delivered_to_date) : '—' }}</td>
                  <td class="text-right" :class="{ 'text-danger font-weight-bold': i.outstanding > 0 }">{{ i.outstanding !== null ? qty(i.outstanding) : '—' }}</td>
                </tr>
              </tbody>
            </table>

            <!-- Proof of delivery -->
            <div v-if="detail.delivered_at" class="border rounded p-3 mb-3">
              <div class="text-primary font-weight-bold mb-1">Proof of delivery</div>
              <div class="small">
                Received by <strong>{{ detail.received_by_name }}</strong><span v-if="detail.received_by_phone"> ({{ detail.received_by_phone }})</span>
                on {{ dt(detail.delivered_at) }}
              </div>
              <div class="small text-secondary">
                {{ methodText(detail.confirmation_method) }}<span v-if="detail.recorded_by_name"> · by {{ detail.recorded_by_name }}</span>
                <span v-if="detail.delivered_lat"> · <a :href="`https://maps.google.com/?q=${detail.delivered_lat},${detail.delivered_lng}`" target="_blank" rel="noopener">see location on map</a></span>
              </div>
              <div v-if="detail.delivery_remarks" class="small mt-1">Remarks: {{ detail.delivery_remarks }}</div>
              <img v-if="detail.received_signature" :src="detail.received_signature" alt="Signature" class="mt-2 border rounded" style="max-height:110px;max-width:100%;background:#fff;">
            </div>
            <div v-else-if="detail.status === 'dispatched'" class="alert alert-secondary small">
              Not confirmed yet. To get it signed: hand this device to the receiver and press <strong>Sign / mark delivered</strong>,
              or send the client and driver their links (<strong>Links &amp; SMS</strong>) so they sign, or confirm with the delivery code, on their own phones.
            </div>

            <!-- Returns -->
            <div v-if="sum.returns && sum.returns.length" class="mb-3">
              <div class="font-weight-bold mb-1">Returns</div>
              <div v-for="(r, k) in sum.returns" :key="k" class="small border-bottom py-1">
                {{ dt(r.at) }} · {{ qty(r.quantity) }} {{ r.unit }} {{ r.product }}<span v-if="r.by"> · {{ r.by }}</span>
                <div class="text-secondary">{{ r.notes }}</div>
              </div>
            </div>
          </div>

          <div class="col-lg-4">
            <!-- Invoice -->
            <div v-if="sum.invoice" class="border rounded p-3 mb-3 small">
              <div class="font-weight-bold mb-1">Invoice <a :href="`/invoices/${sum.invoice.id}`">{{ sum.invoice.number }}</a></div>
              <div>Status: <span class="text-capitalize">{{ sum.invoice.status }}</span></div>
              <div>Total {{ money(sum.invoice.total) }}<span v-if="sum.invoice.paid !== null"> · Paid {{ money(sum.invoice.paid) }}</span></div>
              <div v-if="sum.invoice.balance > 0" class="text-danger">Balance {{ money(sum.invoice.balance) }}</div>
              <div v-if="sum.other_notes && sum.other_notes.length" class="mt-2">
                <div class="text-secondary">Other deliveries on this invoice</div>
                <a v-for="o in sum.other_notes" :key="o.id" href="#" class="d-block" @click.prevent="openDetails(o)">{{ o.number }} · {{ statusText(o.status) }} · {{ dt(o.dispatched_at) }}</a>
              </div>
            </div>

            <!-- Trip & contacts -->
            <div class="border rounded p-3 mb-3 small">
              <div class="font-weight-bold mb-1">Trip</div>
              <div>Driver: {{ detail.driver || '—' }}<span v-if="detail.vehicle"> · {{ detail.vehicle }}</span></div>
              <div v-if="detail.driver_phone">{{ detail.driver_phone }}
                <a :href="`tel:${detail.driver_phone}`" class="ml-1">Call</a> · <a :href="whatsapp(detail.driver_phone)" target="_blank" rel="noopener">WhatsApp</a></div>
              <div class="mt-1">Client: {{ detail.customer_phone || '—' }}
                <span v-if="detail.customer_phone"><a :href="`tel:${detail.customer_phone}`" class="ml-1">Call</a> · <a :href="whatsapp(detail.customer_phone)" target="_blank" rel="noopener">WhatsApp</a></span></div>
              <div v-if="detail.notes" class="mt-1">Notes: {{ detail.notes }}</div>
            </div>

            <!-- Timeline -->
            <div class="border rounded p-3 mb-3 small">
              <div class="font-weight-bold mb-1">Timeline</div>
              <div>Dispatched {{ dt(detail.dispatched_at) }}<span v-if="detail.dispatched_by && detail.dispatched_by.name"> by {{ detail.dispatched_by.name }}</span></div>
              <div v-if="detail.sms_sent_at">SMS links sent {{ dt(detail.sms_sent_at) }}</div>
              <div v-else class="text-secondary">No SMS sent</div>
              <div v-if="detail.code_attempts > 0" :class="{ 'text-danger': detail.code_attempts >= 5 }">{{ detail.code_attempts }} wrong delivery code(s){{ detail.code_attempts >= 5 ? ' — locked, resend the SMS' : '' }}</div>
              <div v-for="(r, k) in (sum.returns || [])" :key="'t' + k">Return {{ dt(r.at) }}</div>
              <div v-if="detail.delivered_at">Delivered {{ dt(detail.delivered_at) }}</div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="d-flex flex-wrap">
          <button v-if="detail.status === 'dispatched'" class="btn btn-primary mr-2 mb-2" @click="fromDetails(openDelivered)"><i class="bi bi-pen mr-1"></i>Sign / mark delivered</button>
          <button v-if="detail.status === 'dispatched'" class="btn btn-secondary mr-2 mb-2" @click="fromDetails(openShare)"><i class="bi bi-chat-dots mr-1"></i>Links &amp; SMS</button>
          <button v-if="sum.invoice && sum.outstanding_lines > 0" class="btn btn-secondary mr-2 mb-2" @click="dispatchRest"><i class="bi bi-truck mr-1"></i>Dispatch the rest</button>
          <a class="btn btn-secondary mr-2 mb-2" :href="printUrl(detail.id)" target="_blank"><i class="bi bi-printer mr-1"></i>Print</a>
          <a v-if="sum.invoice" class="btn btn-secondary mr-2 mb-2" :href="`/invoices/${sum.invoice.id}`"><i class="bi bi-receipt mr-1"></i>Open invoice</a>
          <button class="btn btn-secondary mb-2" @click="fromDetails(openReturn)"><i class="bi bi-arrow-return-left mr-1"></i>Return</button>
        </div>
      </div>
    </b-modal>

    <!-- Dispatch -->
    <b-modal ref="dispatchModal" title="Dispatch invoiced goods" size="lg" hide-footer>
      <form class="form-row align-items-end" @submit.prevent="loadInvoice">
        <div class="col-md-8 mb-2">
          <label class="small text-secondary mb-1">Invoice</label>
          <select class="custom-select" v-model="invoiceId">
            <option value="">Choose an invoice…</option>
            <option v-for="inv in invoices" :key="inv.id" :value="inv.id">{{ inv.number }} — {{ inv.customer ? inv.customer.full_name : 'No customer' }}</option>
          </select>
        </div>
        <div class="col-md-4 mb-2"><button class="btn btn-secondary btn-block" :disabled="!invoiceId">Load lines</button></div>
      </form>

      <div v-if="invoice">
        <table class="table table-sm mt-2">
          <thead><tr><th>Product</th><th class="text-right">Invoiced</th><th class="text-right">Already sent</th><th class="text-right">In stock</th><th style="width:140px">Dispatch now</th></tr></thead>
          <tbody>
            <tr v-for="l in invoice.lines" :key="l.invoice_item_id">
              <td>{{ l.product }} <span v-if="l.track_lots" class="badge badge-secondary">lots</span></td>
              <td class="text-right">{{ qty(l.invoiced) }} {{ l.unit }}</td>
              <td class="text-right">{{ qty(l.dispatched) }}</td>
              <td class="text-right" :class="{ 'text-danger': l.in_stock !== null && Number(l.send) > l.in_stock }">{{ qty(l.in_stock) }}</td>
              <td><input type="number" step="any" min="0" class="form-control" v-model.number="l.send" :max="l.outstanding" /></td>
            </tr>
          </tbody>
        </table>
        <div class="form-row">
          <div class="col-md-8 form-group"><label class="small text-secondary mb-1">Deliver to (town / address)</label><input class="form-control" v-model="meta.destination" placeholder="e.g. Kericho town, Moi Highway" /></div>
          <div class="col-md-4 form-group"><label class="small text-secondary mb-1">Contact at destination</label><input class="form-control" v-model="meta.destination_contact" placeholder="Name" /></div>
        </div>
        <div class="form-row">
          <div class="col-md-4 form-group"><label class="small text-secondary mb-1">Vehicle</label><input class="form-control" v-model="meta.vehicle" /></div>
          <div class="col-md-4 form-group"><label class="small text-secondary mb-1">Driver</label><input class="form-control" v-model="meta.driver" /></div>
          <div class="col-md-4 form-group"><label class="small text-secondary mb-1">Notes</label><input class="form-control" v-model="meta.notes" /></div>
        </div>
        <div class="form-row">
          <div class="col-md-6 form-group"><label class="small text-secondary mb-1">Client phone (for SMS)</label><vue-tel-input v-model="meta.customer_phone" defaultCountry="KE"></vue-tel-input></div>
          <div class="col-md-6 form-group"><label class="small text-secondary mb-1">Driver phone (for SMS)</label><vue-tel-input v-model="meta.driver_phone" defaultCountry="KE"></vue-tel-input></div>
        </div>
        <b-form-checkbox v-model="meta.send_sms" class="mb-2">
          SMS the client (link + delivery code) and the driver (link) now
        </b-form-checkbox>
        <p class="small text-muted">Lot-tracked products are allocated automatically from released batches, earliest expiry first.</p>
        <div v-if="error" class="alert alert-danger">{{ error }}</div>
        <button class="btn btn-primary" :disabled="busy || !sendLines.length" @click="dispatch">Create delivery note</button>
      </div>
    </b-modal>

    <!-- Links & SMS -->
    <b-modal ref="shareModal" :title="shareNote ? 'Share ' + shareNote.number : 'Share'" hide-footer>
      <div v-if="shareNote">
        <p v-if="smsReport" class="small" :class="smsReport.errors.length ? 'text-danger' : 'text-secondary'">
          <span v-if="smsReport.client">Client SMS sent. </span><span v-if="smsReport.driver">Driver SMS sent. </span>
          <span v-for="e in smsReport.errors" :key="e">{{ e }} </span>
        </p>
        <div class="form-group">
          <label class="small text-secondary mb-1">Client link — view, see the invoice and sign</label>
          <div class="input-group">
            <input class="form-control" :value="links.client" readonly />
            <div class="input-group-append"><button class="btn btn-secondary" @click="copy(links.client)">Copy</button></div>
          </div>
        </div>
        <div class="form-group">
          <label class="small text-secondary mb-1">Driver link — client signs on the driver's phone, or driver enters the client's code</label>
          <div class="input-group">
            <input class="form-control" :value="links.driver" readonly />
            <div class="input-group-append"><button class="btn btn-secondary" @click="copy(links.driver)">Copy</button></div>
          </div>
        </div>
        <hr />
        <div class="form-row">
          <div class="col-md-6 form-group"><label class="small text-secondary mb-1">Client phone</label><vue-tel-input v-model="share.customer_phone" defaultCountry="KE"></vue-tel-input></div>
          <div class="col-md-6 form-group"><label class="small text-secondary mb-1">Driver phone</label><vue-tel-input v-model="share.driver_phone" defaultCountry="KE"></vue-tel-input></div>
        </div>
        <p class="small text-muted">Sending to the client issues a new delivery code; the old one stops working.</p>
        <div v-if="error" class="alert alert-danger">{{ error }}</div>
        <button class="btn btn-primary mr-1 mb-1" :disabled="busy" @click="resend('both')">SMS client &amp; driver</button>
        <button class="btn btn-secondary mr-1 mb-1" :disabled="busy" @click="resend('client')">Client only</button>
        <button class="btn btn-secondary mb-1" :disabled="busy" @click="resend('driver')">Driver only</button>
      </div>
    </b-modal>

    <!-- Proof of delivery -->
    <b-modal ref="deliveredModal" :title="deliveredNote ? 'Delivered: ' + deliveredNote.number : 'Delivered'" hide-footer>
      <div v-if="deliveredNote">
        <p class="small text-secondary">{{ deliveredNote.destination || 'No destination recorded' }} · {{ deliveredNote.customer && deliveredNote.customer.full_name }}</p>
        <p class="small">Hand this screen to the person receiving the goods: they type their name and sign in the box below.</p>
        <div class="form-group"><label class="small text-secondary mb-1">Received by (name)</label><input class="form-control" v-model="delivered.received_by_name" /></div>
        <div class="form-group"><label class="small text-secondary mb-1">Their phone</label><vue-tel-input v-model="delivered.received_by_phone" defaultCountry="KE"></vue-tel-input></div>
        <div class="form-group"><label class="small text-secondary mb-1">Remarks</label><input class="form-control" v-model="delivered.delivery_remarks" placeholder="e.g. All bags received in good condition" /></div>
        <div class="form-group">
          <label class="small text-secondary mb-1">Signature (optional — the client signs on this screen)</label>
          <signature-pad v-model="delivered.signature"></signature-pad>
        </div>
        <div v-if="error" class="alert alert-danger">{{ error }}</div>
        <button class="btn btn-primary" :disabled="busy || !delivered.received_by_name" @click="saveDelivered">Confirm delivery</button>
      </div>
    </b-modal>

    <!-- Return -->
    <b-modal ref="returnModal" :title="returnNote ? 'Return against ' + returnNote.number : 'Return'" hide-footer>
      <div v-if="returnNote">
        <table class="table table-sm">
          <thead><tr><th>Product</th><th class="text-right">Open</th><th style="width:130px">Return</th></tr></thead>
          <tbody>
            <tr v-for="i in returnNote.items" :key="i.id">
              <td>{{ i.product && i.product.product }}</td>
              <td class="text-right">{{ qty(i.quantity - i.returned_quantity) }}</td>
              <td><input type="number" step="any" min="0" class="form-control" v-model.number="i.ret" /></td>
            </tr>
          </tbody>
        </table>
        <div class="form-group"><label class="small text-secondary mb-1">Reason</label><input class="form-control" v-model="returnReason" /></div>
        <div v-if="error" class="alert alert-danger">{{ error }}</div>
        <button class="btn btn-primary" :disabled="busy || !returnReason" @click="saveReturn">Record return</button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { apiError, fmtQty, fmtMoney } from './mfgHelpers';
import SignaturePad from './SignaturePad.vue';

const blankMeta = () => ({ vehicle: '', driver: '', notes: '', destination: '', destination_contact: '', customer_phone: '', driver_phone: '', send_sms: true });

export default {
  name: 'DeliveryNotesPanel',
  components: { SignaturePad },
  props: { standalone: { type: Boolean, default: false } },
  data() {
    return {
      notes: [], loading: false, busy: false, error: null, search: '', timer: null,
      invoices: [], invoiceId: '', invoice: null, meta: blankMeta(),
      deliveredNote: null, delivered: { received_by_name: '', received_by_phone: '', delivery_remarks: '', signature: null },
      returnNote: null, returnReason: '',
      detail: null, detailLoading: false,
      shareNote: null, links: { client: '', driver: '' }, share: { customer_phone: '', driver_phone: '' }, smsReport: null,
    };
  },
  computed: {
    ...mapGetters(['userDetails']),
    sum() { return (this.detail && this.detail.summary) || {}; },
    sendLines() { return this.invoice ? this.invoice.lines.filter(l => Number(l.send) > 0) : []; },
    // Businesses with invoices use the general Deliveries endpoints; a manufacturer
    // without the invoices add-on keeps the manufacturing ones.
    hasInvoices() { return !!(this.userDetails && this.userDetails.company && this.userDetails.company.companysettings && this.userDetails.company.companysettings.has_invoices); },
    api() { return this.hasInvoices ? '/api/delivery-notes' : '/api/manufacturing/delivery-notes'; },
  },
  mounted() {
    this.load();
    // /deliveries?invoice=<id> (from the invoice page) opens the dispatch form for that invoice.
    const inv = new URLSearchParams(window.location.search).get('invoice');
    if (inv) this.openDispatch(inv);
  },
  methods: {
    qty: fmtQty,
    money: fmtMoney,
    statusBadge(st) { return st === 'delivered' ? 'badge-primary' : st === 'dispatched' ? 'badge-dark' : 'badge-secondary'; },
    statusText(st) { return st === 'dispatched' ? 'in transit' : String(st || '').replace('_', ' '); },
    methodText(m) { return { signature: 'Signed on screen', code: 'Confirmed with the delivery code', staff: 'Recorded by staff' }[m] || 'Recorded'; },
    whatsapp(phone) {
      let d = String(phone || '').replace(/\D/g, '');
      if (d.startsWith('0')) d = '254' + d.slice(1);
      return `https://wa.me/${d}`;
    },
    dispatchRest() {
      const inv = this.sum.invoice.id;
      this.$refs.detailsModal.hide();
      this.openDispatch(inv);
    },
    dt(v) { return v ? String(v).substring(0, 16).replace('T', ' ') : '—'; },
    lotsOf(item) {
      return [...new Set((item.ledger_entries || []).map(e => e.lot && e.lot.lot_number).filter(Boolean))].join(', ');
    },
    async openDetails(note) {
      this.detail = null; this.detailLoading = true;
      this.$refs.detailsModal.show();
      try { this.detail = (await axios.get(`${this.api}/${note.id}`)).data; }
      catch (e) { this.$refs.detailsModal.hide(); this.$toasts && this.$toasts.push({ type: 'error', message: apiError(e) }); }
      finally { this.detailLoading = false; }
    },
    // Actions opened from the details view work on the same note, then the details close.
    fromDetails(action) {
      const note = this.detail;
      this.$refs.detailsModal.hide();
      action.call(this, note);
    },
    methodLabel(m) { return { signature: 'signed', code: 'code', staff: 'recorded by staff' }[m] || m; },
    printUrl(id) { return this.hasInvoices ? `/deliveries/${id}/print` : `/manufacturing/delivery-notes/${id}/print`; },
    debouncedLoad() { clearTimeout(this.timer); this.timer = setTimeout(this.load, 350); },
    async load() {
      this.loading = true;
      try { this.notes = (await axios.get(this.api, { params: { search: this.search } })).data.data; } finally { this.loading = false; }
    },
    async openDispatch(preselect) {
      this.error = null; this.invoice = null; this.invoiceId = ''; this.meta = blankMeta();
      try {
        const { data } = await axios.get('/api/invoices', { params: { per_page: 50 } });
        this.invoices = (data.data || data).filter(i => !['cancelled', 'draft'].includes(i.status));
      } catch (e) { this.invoices = []; }
      this.$refs.dispatchModal.show();
      if (typeof preselect === 'string' && preselect) {
        this.invoiceId = preselect;
        if (!this.invoices.find(i => i.id === preselect)) this.invoices.unshift({ id: preselect, number: 'Selected invoice', customer: null });
        this.loadInvoice();
      }
    },
    async loadInvoice() {
      this.error = null;
      try {
        const { data } = await axios.get(`${this.api}/invoice/${this.invoiceId}`);
        data.lines.forEach(l => this.$set(l, 'send', Math.max(l.outstanding, 0)));
        this.invoice = data;
        const inv = this.invoices.find(i => i.id === this.invoiceId);
        if (inv && !inv.customer && data.invoice) inv.number = data.invoice.number;
        if (!this.meta.destination && data.customer && data.customer.address) this.meta.destination = data.customer.address;
        if (!this.meta.customer_phone && data.customer && data.customer.phone) this.meta.customer_phone = data.customer.phone;
      } catch (e) { this.error = apiError(e); }
    },
    async dispatch() {
      this.busy = true; this.error = null;
      try {
        const { data } = await axios.post(this.api, {
          invoice_id: this.invoiceId, ...this.meta,
          items: this.sendLines.map(l => ({ invoice_item_id: l.invoice_item_id, quantity: l.send })),
        });
        this.$toasts && this.$toasts.push({ type: 'success', message: data.message });
        this.$refs.dispatchModal.hide();
        this.load();
        // Show the links (and any SMS problem) straight away.
        this.openShare(data.delivery_note, data.links, data.sms);
      } catch (e) { this.error = apiError(e); } finally { this.busy = false; }
    },
    async openShare(note, links = null, sms = null) {
      this.error = null; this.smsReport = sms; this.shareNote = note;
      this.share = { customer_phone: note.customer_phone || '', driver_phone: note.driver_phone || '' };
      this.links = links || (await axios.get(`${this.api}/${note.id}/links`)).data;
      this.$refs.shareModal.show();
    },
    async resend(to) {
      this.busy = true; this.error = null;
      try {
        const { data } = await axios.post(`${this.api}/${this.shareNote.id}/sms`, { ...this.share, to });
        this.smsReport = data.sms;
        this.$toasts && this.$toasts.push({ type: data.sms.client || data.sms.driver ? 'success' : 'error', message: data.message });
        this.load();
      } catch (e) { this.error = apiError(e); } finally { this.busy = false; }
    },
    async copy(text) {
      try { await navigator.clipboard.writeText(text); this.$toasts && this.$toasts.push({ type: 'success', message: 'Link copied.' }); }
      catch (e) { this.error = 'Could not copy — select the link and copy it manually.'; }
    },
    openDelivered(note) {
      this.error = null;
      this.deliveredNote = note;
      this.delivered = { received_by_name: '', received_by_phone: '', delivery_remarks: '', signature: null };
      this.$refs.deliveredModal.show();
    },
    async saveDelivered() {
      this.busy = true; this.error = null;
      try {
        const { data } = await axios.patch(`${this.api}/${this.deliveredNote.id}/delivered`, this.delivered);
        this.$toasts && this.$toasts.push({ type: 'success', message: data.message });
        this.$refs.deliveredModal.hide();
        this.load();
      } catch (e) { this.error = apiError(e); } finally { this.busy = false; }
    },
    async openReturn(note) {
      this.error = null; this.returnReason = '';
      const { data } = await axios.get(`${this.api}/${note.id}`);
      data.items.forEach(i => this.$set(i, 'ret', 0));
      this.returnNote = data;
      this.$refs.returnModal.show();
    },
    async saveReturn() {
      this.busy = true; this.error = null;
      try {
        await axios.post(`${this.api}/${this.returnNote.id}/returns`, {
          reason: this.returnReason,
          items: this.returnNote.items.filter(i => Number(i.ret) > 0).map(i => ({ delivery_note_item_id: i.id, quantity: i.ret })),
        });
        this.$toasts && this.$toasts.push({ type: 'success', message: 'Return recorded; stock restored.' });
        this.$refs.returnModal.hide();
        this.load();
      } catch (e) { this.error = apiError(e); } finally { this.busy = false; }
    },
  },
};
</script>

<style scoped>
.dn-row { cursor: pointer; }
.dn-stats { display: flex; flex-wrap: wrap; gap: 24px; }
.dn-stat-label { font-size: .75rem; color: #6c757d; text-transform: uppercase; letter-spacing: .3px; }
.dn-stat-value { font-size: 1.15rem; font-weight: 600; }
.mfg-header { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
.mfg-title { margin: 0; }
.filter-bar { display: flex; gap: 8px; margin-bottom: 12px; }
.mfg-box { background: #fff; border-radius: 8px; }
</style>
